/**
 * Shift Management Utilities
 * 
 * Utility functions for shift management including user permissions,
 * date calculations, and data formatting.
 */

import { SHIFT_EDIT_PERMISSIONS } from '../config/shiftConfig';

/**
 * Check if the current user is in the NOC or admin group
 * @param {Array} groups - User's groups/roles from authentication (can be strings or objects)
 * @returns {boolean} - True if user is in NOC or admin group
 */
export const isNOCUser = (groups = []) => {
  return groups.some(group => {
    // Handle both string roles (from Keycloak realm_access.roles) and group objects
    const groupName = typeof group === 'string' 
      ? group.toLowerCase() 
      : (group.name || group.id || '').toLowerCase();
    return groupName === 'noc' || groupName === 'admin';
  });
};

/**
 * Check if the current user has permission to edit shifts
 * @param {string} username - Current user's username
 * @returns {boolean} - True if user has edit permissions
 */
export const hasShiftEditPermission = (username) => {
  return SHIFT_EDIT_PERMISSIONS.users.includes(username);
};

/**
 * Get the current two weeks' date range (Monday to Sunday x2)
 * @param {Date} date - Reference date (defaults to today)
 * @returns {Object} - { start: Date, end: Date, dates: [] }
 */
export const getCurrentWeekRange = (date = new Date()) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust to Monday
  
  const start = new Date(d.setDate(diff));
  const end = new Date(start);
  end.setDate(end.getDate() + 13); // 14 days = 2 weeks
  
  const dates = [];
  for (let i = 0; i < 14; i++) {
    const currentDate = new Date(start);
    currentDate.setDate(currentDate.getDate() + i);
    dates.push(currentDate);
  }
  
  return { start, end, dates };
};

/**
 * Format date to YYYY-MM-DD format
 * @param {Date} date - Date to format
 * @returns {string} - Formatted date string
 */
export const formatDateToAPI = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Format date for display (e.g., "Mon, Mar 4")
 * @param {Date} date - Date to format
 * @returns {string} - Formatted date string
 */
export const formatDateForDisplay = (date) => {
  const d = new Date(date);
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  return `${days[d.getDay()]}, ${months[d.getMonth()]} ${d.getDate()}`;
};

/**
 * Parse date string (YYYY-MM-DD) to Date object in local timezone
 * This prevents timezone offset issues that occur with new Date("YYYY-MM-DD")
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {Date} - Date object in local timezone
 */
export const parseDateString = (dateString) => {
  if (!dateString) return null;
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Format date string for display with proper timezone handling
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {string} - Formatted date string (e.g., "3/12/2026")
 */
export const formatDateStringForDisplay = (dateString) => {
  const date = parseDateString(dateString);
  if (!date) return '';
  return date.toLocaleDateString();
};

/**
 * Check if a user is on time off on a specific date
 * @param {Array} timeOffRecords - Array of time off records
 * @param {Date} date - Date to check
 * @returns {Object|null} - Time off record if on time off, null otherwise
 */
export const isUserOnTimeOff = (timeOffRecords = [], date) => {
  const checkDate = new Date(date);
  return timeOffRecords.find(record => {
    const startDate = new Date(record.start_date);
    const endDate = new Date(record.end_date);
    return checkDate >= startDate && checkDate <= endDate;
  }) || null;
};

/**
 * Group shift assignments by date
 * @param {Array} assignments - Array of shift assignments
 * @returns {Object} - Assignments grouped by date
 */
export const groupAssignmentsByDate = (assignments = []) => {
  return assignments.reduce((acc, assignment) => {
    const date = assignment.assignment_date;
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(assignment);
    return acc;
  }, {});
};

/**
 * Group shift assignments by user
 * @param {Array} assignments - Array of shift assignments
 * @returns {Object} - Assignments grouped by user
 */
export const groupAssignmentsByUser = (assignments = []) => {
  return assignments.reduce((acc, assignment) => {
    const userId = assignment.user;
    if (!acc[userId]) {
      acc[userId] = [];
    }
    acc[userId].push(assignment);
    return acc;
  }, {});
};

const shiftUtils = {
  isNOCUser,
  hasShiftEditPermission,
  getCurrentWeekRange,
  formatDateToAPI,
  formatDateForDisplay,
  isUserOnTimeOff,
  groupAssignmentsByDate,
  groupAssignmentsByUser,
};

export default shiftUtils;
